import { MARKET_ASSET_CLASS_LABELS } from '../products/productConstants';

/** 後端合約時間查詢最大跨度（天） */
export const MAX_CONTRACT_QUERY_DAYS = 7;

/** 週期選項（對齊 FuturesRoundPeriod） */
export const FUTURES_ROUND_PERIODS = [
  { value: '_60s', label: '60秒' },
  { value: '_120s', label: '120秒' },
  { value: '_5m', label: '5分钟' },
  { value: '_10m', label: '10分钟' },
  { value: '_20m', label: '20分钟' },
  { value: '_30m', label: '30分钟' },
];

export const FUTURES_ROUND_STATE_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'OPEN', label: '進行中' },
  { value: 'CLOSED', label: '已封盤' },
  { value: 'SETTLED', label: '已結算' },
  { value: 'CANCELLED', label: '已取消' },
];

export const FUTURES_ROUND_STATE_LABELS = Object.fromEntries(
  FUTURES_ROUND_STATE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label])
);

export const FUTURES_ROUND_PERIOD_LABELS = Object.fromEntries(
  FUTURES_ROUND_PERIODS.map((o) => [o.value, o.label])
);

export const FUTURES_OVERRIDE_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'DEFAULT', label: '跟隨全局' },
  { value: 'GLOBAL_CONTROL', label: '系統控' },
  { value: 'GO_LONG', label: '做多贏' },
  { value: 'GO_SHORT', label: '做空贏' },
  { value: 'PROMISE_PROFIT', label: '包贏' },
  { value: 'DO_NOTHING', label: '不控' },
  { value: 'USER', label: '用戶控' },
];

export const FUTURES_ASSET_CLASS_OPTIONS = [
  { value: '', label: '全部' },
  ...Object.entries(MARKET_ASSET_CLASS_LABELS).map(([value, label]) => ({ value, label })),
];

export const OVERRIDE_LABELS = Object.fromEntries(
  FUTURES_OVERRIDE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label])
);

export function formatAmount(value) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toFixed(2);
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** GET 查詢參數：ISO 格式（對齊 Spring @DateTimeFormat） */
export function toApiQueryDateTime(date) {
  return [
    date.getFullYear(),
    pad2(date.getMonth() + 1),
    pad2(date.getDate()),
  ].join('-') + `T${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

/** POST body：空格分隔（對齊 @JsonFormat） */
export function toApiBodyDateTime(date) {
  return [
    date.getFullYear(),
    pad2(date.getMonth() + 1),
    pad2(date.getDate()),
  ].join('-') + ` ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

export function toInputDateTime(date) {
  return [
    date.getFullYear(),
    pad2(date.getMonth() + 1),
    pad2(date.getDate()),
  ].join('-') + `T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** datetime-local → GET 查詢用 ISO */
export function inputDateTimeToQueryApi(value) {
  if (!value) {
    return '';
  }
  const normalized = value.trim().replace(' ', 'T');
  if (normalized.length === 16) {
    return `${normalized}:00`;
  }
  return normalized;
}

/** datetime-local → POST body */
export function inputDateTimeToBodyApi(value) {
  const query = inputDateTimeToQueryApi(value);
  if (!query) {
    return '';
  }
  return query.replace('T', ' ');
}

function calendarDaysBetween(fromInput, toInput) {
  const fromDate = fromInput.slice(0, 10);
  const toDate = toInput.slice(0, 10);
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T00:00:00`);
  return Math.round((to - from) / (24 * 60 * 60 * 1000));
}

/** 預設：今日 00:00 起至 +6 日 23:59（涵蓋預生成週期，且不超過 7 天限制） */
export function defaultContractRange() {
  const now = new Date();
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);

  const to = new Date(from);
  to.setDate(to.getDate() + MAX_CONTRACT_QUERY_DAYS - 1);
  to.setHours(23, 59, 0, 0);

  return {
    contractTimeFromInput: toInputDateTime(from),
    contractTimeToInput: toInputDateTime(to),
  };
}

export function validateContractRange(fromInput, toInput) {
  if (!fromInput || !toInput) {
    return '請選擇合約時間起迄';
  }
  const from = new Date(inputDateTimeToQueryApi(fromInput));
  const to = new Date(inputDateTimeToQueryApi(toInput));
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return '合約時間格式不正確';
  }
  if (from > to) {
    return '合約時間起不可晚於迄';
  }
  const days = calendarDaysBetween(fromInput, toInput);
  if (days > MAX_CONTRACT_QUERY_DAYS) {
    return `合約時間跨度不可超過 ${MAX_CONTRACT_QUERY_DAYS} 天`;
  }
  return null;
}

export function buildPageParams(pageNo, filters) {
  const params = { pageNo, pageSize: 15 };

  const symbol = filters.marketSymbolCode?.trim();
  if (symbol) {
    params.marketSymbolCode = symbol.toUpperCase();
  }
  if (filters.periodType) {
    params.periodType = filters.periodType;
  }
  if (filters.state) {
    params.state = filters.state;
  }
  const roundCode = filters.roundCode?.trim();
  if (roundCode) {
    params.roundCode = roundCode;
  }
  if (filters.overrideMethod) {
    params.overrideMethod = filters.overrideMethod;
  }
  if (filters.assetClass) {
    params.assetClass = filters.assetClass;
  }
  if (filters.playType) {
    params.playType = filters.playType;
  }

  const contractTimeFrom = inputDateTimeToQueryApi(filters.contractTimeFromInput);
  const contractTimeTo = inputDateTimeToQueryApi(filters.contractTimeToInput);
  if (contractTimeFrom) {
    params.contractTimeFrom = contractTimeFrom;
  }
  if (contractTimeTo) {
    params.contractTimeTo = contractTimeTo;
  }

  return params;
}
